import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { notify } from "@/utils";
import { logError } from "@/utils/logger";

import {
  isAudioPrepared,
  prepareAudio,
  useVoicePeaks,
} from "../../hooks/useAudioPlayer";
import { useVoicePlaybackContext } from "../../store/voicePlayback";
import { formatVoiceDuration } from "../../utils/voice";
import { VoicePlayButton } from "./VoiceButtons";
import VoiceWaveform from "./VoiceWaveform";

function placeholderPeaks(seed: number) {
  const peaks: number[] = [];
  let state = Math.abs(seed) % 97 || 1;

  for (let index = 0; index < 48; index += 1) {
    state = (state * 37 + 17) % 97;
    peaks.push(0.3 + 0.6 * Math.abs(Math.sin(index / 2.6 + state)));
  }

  return peaks;
}

interface VoiceMessagePlayerProps {
  messageId: number;
  url: string;
  duration?: number | null;
  isMe: boolean;
  userName: string;
  userPosition?: string | null;
}

export default function VoiceMessagePlayer({
  messageId,
  url,
  duration,
  isMe,
  userName,
  userPosition,
}: VoiceMessagePlayerProps) {
  const { t } = useTranslation("messenger.user");
  const urlRef = useRef(url);
  urlRef.current = url;
  const [downloading, setDownloading] = useState(false);
  const [ready, setReady] = useState(() => isAudioPrepared(url));

  useEffect(() => {
    setReady(isAudioPrepared(url));
    setDownloading(false);
  }, [url]);

  const waveform = useVoicePeaks(url, ready);
  const placeholder = useMemo(() => placeholderPeaks(messageId), [messageId]);
  const playVoice = useVoicePlaybackContext((state) => state.playVoice);
  const togglePlayback = useVoicePlaybackContext((state) => state.toggle);
  const seek = useVoicePlaybackContext((state) => state.seek);
  const isActive = useVoicePlaybackContext(
    (state) => state.active?.messageId === messageId,
  );
  const isPlaying = useVoicePlaybackContext((state) =>
    state.active?.messageId === messageId ? state.isPlaying : false,
  );
  const currentTime = useVoicePlaybackContext((state) =>
    state.active?.messageId === messageId ? state.currentTime : 0,
  );
  const playbackDuration = useVoicePlaybackContext((state) =>
    state.active?.messageId === messageId ? state.duration : 0,
  );
  const progress = useVoicePlaybackContext((state) =>
    state.active?.messageId === messageId ? state.progress : 0,
  );

  const totalSeconds = playbackDuration || waveform.duration || duration || 0;
  const shownSeconds =
    isPlaying || currentTime > 0 ? currentTime : totalSeconds;

  const play = () => {
    playVoice({ messageId, url, userName, userPosition });
  };

  const onPress = () => {
    if (downloading) return;

    if (!ready) {
      setDownloading(true);
      prepareAudio(url)
        .then(() => {
          if (urlRef.current !== url) return;
          setReady(true);
          play();
        })
        .catch((error: unknown) => {
          logError(error, { context: "voice_download" });
          notify.error(t("Couldn't download voice message"));
        })
        .finally(() => setDownloading(false));
      return;
    }

    if (isActive) togglePlayback();
    else play();
  };

  return (
    <div className="flex w-full min-w-48 flex-col gap-1">
      <div className="flex items-center gap-2 rtl:flex-row-reverse">
        <VoicePlayButton
          isPlaying={isPlaying}
          pending={downloading}
          download={!ready}
          onClick={(event) => {
            event.stopPropagation();
            onPress();
          }}
          label={
            downloading
              ? t("Download")
              : !ready
                ? t("Download")
                : isPlaying
                  ? t("Pause")
                  : t("Play")
          }
          className={cn("size-8", isMe && "text-primary-600 bg-white")}
        />
        <VoiceWaveform
          peaks={waveform.peaks.length > 0 ? waveform.peaks : placeholder}
          progress={progress}
          onSeek={isActive ? seek : undefined}
          variant={isMe ? "light" : "default"}
        />
      </div>
      <div className="flex items-center gap-1 rtl:flex-row-reverse">
        <span
          className={cn(
            "voice-time text-xs",
            isMe ? "text-white" : "text-gray-600",
          )}
        >
          {formatVoiceDuration(shownSeconds * 1000)}
        </span>
      </div>
    </div>
  );
}
